/**
 * "Continue Adventure" — the one button (docs/rpg.md §34–35, §48, §52).
 *
 * `docs/rpg.md` §52 states the whole job of this file in one line:
 *
 *   > Always give the student a clear answer to: "What should I do next?"
 *
 * The platform already answers it. `recommendNext` in `@yuan-math/learning` is
 * the recommendation engine — repair before review before continue before
 * advance, repair capped at two so a home page is not four "you are behind"
 * cards, `advance` ranked by distance from the level the student works at so a
 * Grade 12 learner is never offered Telling Time. Every one of those rules was
 * a bug first, and every one of them is documented in `CLAUDE.md`.
 *
 * **So this file re-ranks nothing.** It takes the engine's ordered list and
 * dresses it: which kingdom the concept lives in, which accepted quest the work
 * belongs to, what the card should be called, where the button goes. A second
 * opinion about what to study next — a weight nudged for the kingdom the
 * student is standing in, a repair demoted because it reads discouragingly —
 * would be a second recommendation engine, disagreeing with the dashboard's
 * about the same student on the same morning.
 *
 * The one thing the story may never change is the *reason*. A step's title is
 * fantasy and its reason is mathematics, taken verbatim from the recommendation
 * (§48: the engine says *why*, and the why is the point). "An old skill calls
 * for you" is a nicer way to say "review", but under it stands the engine's own
 * sentence about retention, unedited.
 *
 * `registry` is a parameter for the reason every other parameter in this
 * package is one: `content/` sits above `packages/world`, so the curriculum
 * arrives from the caller rather than from an import.
 */

import type { CurriculumRegistry } from '@yuan-math/curriculum';
import {
  conceptLevelOrder,
  knownSet,
  recommendNext,
  workingLevel,
  type LearnerProfile,
  type Recommendation,
  type RecommendationKind,
} from '@yuan-math/learning';
import { questProgress, type QuestDef, type QuestObjective } from './quests.ts';
import { conceptKingdoms, worldState, type KingdomState } from './state.ts';
import type { Kingdom } from './types.ts';

/**
 * One thing to go and do, in the adventure's vocabulary.
 *
 * `kind` is the engine's own kind, carried through so a card can badge itself
 * without re-deriving the classification from the words in the title.
 */
export type AdventureStep = {
  kind: RecommendationKind;
  /** The framing, naming the concept: "An old skill calls for you — Rounding". */
  title: string;
  /** The engine's reason, verbatim. Mathematical, and never rewritten here. */
  reason: string;
  conceptId: string;
  /**
   * The kingdom holding the concept. Optional because a caller may pass a
   * partial world — every *available* concept belongs to exactly one region of
   * exactly one kingdom, and `validateWorld` is what enforces it.
   */
  kingdomId?: string;
  /** The accepted quest this work counts towards, when one does. */
  questId?: string;
  /**
   * Where the button goes: the quest page when the work belongs to a quest,
   * the concept page for something the student has not met yet — the door that
   * offers the lesson first — and the practice page for everything else, which
   * is work already begun.
   */
  href: string;
  /**
   * How many of the concept's direct prerequisites are in `knownSet`, which is
   * the sentence §35 asks for underneath the button ("you have mastered 82% of
   * the prerequisite skills"). Direct rather than transitive: the transitive
   * closure of a Grade 12 concept is a hundred concepts and would read as a
   * high percentage for everybody. `knownSet` rather than mastery for the same
   * reason the seals use it — see `types.ts`.
   */
  prerequisites: { known: number; total: number };
};

/**
 * The ordered short list behind the Continue Adventure button: the first step
 * is the button, the rest are the cards under it.
 *
 * Empty only when the engine has nothing to say — a student who knows every
 * available concept and has nothing due back.
 */
export function continueAdventure(
  kingdoms: readonly Kingdom[],
  quests: readonly QuestDef[],
  registry: CurriculumRegistry,
  profile: LearnerProfile,
  now: number,
  limit = 4,
): AdventureStep[] {
  const recommendations = recommendNext({ registry, profile, now }, limit);
  if (recommendations.length === 0) return [];

  const kingdomOfConcept = conceptKingdoms(kingdoms);
  const accepted = acceptedQuests(quests, profile, now);
  const known = knownSet(profile.concepts, now);

  return recommendations.map((recommendation) => {
    const { conceptId } = recommendation;
    const conceptTitle = registry.concept(conceptId)?.title ?? conceptId;
    const quest = accepted.find((candidate) => covers(candidate.objective, conceptId, registry));
    const kingdomId = kingdomOfConcept.get(conceptId);
    const prerequisiteIds = registry.graph.prerequisites.get(conceptId) ?? [];

    return {
      kind: recommendation.kind,
      title: titleFor(recommendation, conceptTitle, quest),
      reason: recommendation.reason,
      conceptId,
      ...(kingdomId ? { kingdomId } : {}),
      ...(quest ? { questId: quest.id } : {}),
      href: hrefFor(recommendation, quest),
      prerequisites: {
        known: prerequisiteIds.filter((id) => known.has(id)).length,
        total: prerequisiteIds.length,
      },
    };
  });
}

/**
 * How far down the recommendation list to look for a forward step. Repairs are
 * capped at two, but a student can have a long run of reviews come due at once,
 * and "where am I" must not be answered by the road-order fallback merely
 * because a Tuesday was busy.
 */
const FRONTIER_LOOKAHEAD = 12;

/**
 * Where the student is standing: the kingdom holding their frontier — the next
 * piece of mathematics they have not learned yet.
 *
 * This used to be "the lowest-order kingdom whose story is open and unfinished",
 * which reads as a position on a road and is right for exactly one student: the
 * one who started at the beginning and walked. A placement-tested student does
 * not have a position on a road. Their frontier is *ragged* — a Grade 10
 * placement opens Starter Village and the Algebra Kingdom, leaves Geometry and
 * Trigonometry sealed, and scatters the work the engine actually recommends
 * across five kingdoms at once. "Lowest open" picks Starter Village out of that
 * every time, and picks it forever: the placement credits what the ladder asked
 * about and what those answers imply, which is never quite all fourteen concepts
 * of the tutorial village, so the village never finishes and the student is
 * greeted with counting for as long as they use the platform. It is the same bug
 * `applyDiagnostic` was written to kill on the dashboard — "I was placed at
 * Grade 12 and my home page wants me to practise counting" — surviving in the
 * one part of the app that answered the question with geography instead of with
 * the recommendation engine.
 *
 * So it asks the engine, exactly as `continueAdventure` does, and re-ranks
 * nothing. The frontier is the first `continue` or `advance` in the engine's own
 * order: those two kinds are the ones that mean "new ground", and they are what
 * `recommendNext` derives from `knownSet` and the placed level. A `repair` or a
 * `review` is deliberately *not* a position — it is a detour from one, and
 * `AdventureHomePage` has a line for exactly that case ("this step leads out of
 * the kingdom you are in"). Letting a review decide where the student is standing
 * would walk them backwards across the map every time one came due.
 *
 * A sealed kingdom can be the answer, and that is not a contradiction. A seal is
 * a story state and never access control (`types.ts`): it withholds the quest
 * line, the boss and the reward, never a lesson. A student whose mathematics is
 * in Trigonometry *is* in Trigonometry, and telling them so — with the seal
 * stated beside it as mathematics — is the honest answer. Telling them they are
 * in Starter Village is not.
 *
 * The road-order rule stays as the fallback, for the student the engine has
 * nothing forward to say to: with every open kingdom cleared, the next sealed
 * one is where they are heading; with nothing sealed either, they have finished
 * the world and stand in the last of it. A brand-new student does not reach it —
 * the engine's first `advance` for an empty profile is in Starter Village, which
 * is where they should be.
 */
export function currentKingdom(
  kingdoms: readonly Kingdom[],
  registry: CurriculumRegistry,
  profile: LearnerProfile,
  now: number,
): { kingdom: Kingdom; state: KingdomState } | undefined {
  const states = worldState(kingdoms, registry.graph, profile.concepts, profile.rpg?.clearedBossIds ?? [], now);
  const byId = new Map(kingdoms.map((kingdom) => [kingdom.id, kingdom]));

  const found = (state: KingdomState | undefined): { kingdom: Kingdom; state: KingdomState } | undefined => {
    const kingdom = state ? byId.get(state.kingdomId) : undefined;
    return kingdom && state ? { kingdom, state } : undefined;
  };

  const kingdomOfConcept = conceptKingdoms(kingdoms);
  const stateOf = new Map(states.map((state) => [state.kingdomId, state]));

  /*
   * The forward steps, nearest to where the student is working first.
   *
   * `workingLevel` is the platform's single answer to "what level is this
   * student at" — their stated level, else the placement test's estimate, else
   * the hardest thing they have actually done — and it is the same number
   * `advance` is already ranked by. Sorting by distance from it is what stops a
   * `continue` hijacking the answer: a `continue` outranks every `advance` by
   * design (it is unfinished business, and finishing it matters), but it is
   * ranked by struggle rather than by level, so one wobbly session on Counting
   * put a Grade 12 student's `continue:counting` above every advance in the
   * engine's order — and reading "the first forward step" off that list put
   * them back in Starter Village. The recommendation was right; taking it as a
   * *position* was not.
   *
   * Ties keep the engine's own order, so priority still decides between two
   * steps at the same level.
   */
  const target = workingLevel({ registry, profile, now });
  const forward = recommendNext({ registry, profile, now }, FRONTIER_LOOKAHEAD)
    .filter(
      (recommendation) =>
        (recommendation.kind === 'continue' || recommendation.kind === 'advance') &&
        kingdomOfConcept.has(recommendation.conceptId),
    )
    .map((recommendation, index) => ({
      recommendation,
      index,
      distance: Math.abs(conceptLevelOrder(registry, recommendation.conceptId) - target),
    }))
    .sort((a, b) => a.distance - b.distance || a.index - b.index)[0];

  const here = forward
    ? found(stateOf.get(kingdomOfConcept.get(forward.recommendation.conceptId) as string))
    : undefined;
  if (here) return here;

  // `worldState` returns kingdoms in `order`, so the first match is the lowest.
  const first = (wanted: KingdomState['status']) => states.find((candidate) => candidate.status === wanted);

  return (
    found(first('open')) ??
    found(first('sealed')) ??
    found([...states].reverse().find((candidate) => candidate.status === 'cleared'))
  );
}

/* -------------------------------------------------------------------------- */

/**
 * The quests the student has taken on and not yet finished, in board order:
 * the main chain first, then the errands — the same comparator the quest board
 * sorts by, so the quest a step names is the one nearest the top of the board
 * rather than whichever the catalogue happened to list first.
 *
 * `accepted` only. A `complete` quest's objective is already met, so framing
 * more practice as continuing it would be a promise the bar cannot keep; a
 * `locked` or `available` one was never taken on, and a recommendation is not
 * the place to start somebody's story for them.
 */
function acceptedQuests(quests: readonly QuestDef[], profile: LearnerProfile, now: number): QuestDef[] {
  return quests
    .filter((quest) => questProgress(quest, quests, profile, now).state === 'accepted')
    .sort((a, b) => {
      if ((a.kind === 'main') !== (b.kind === 'main')) return a.kind === 'main' ? -1 : 1;
      if (a.order !== b.order) return a.order - b.order;
      return a.id.localeCompare(b.id);
    });
}

/**
 * Does finishing this quest involve this concept?
 *
 * A `clear-quests` objective names quests rather than mathematics, so it covers
 * nothing: a capstone is finished by finishing its chain, and no amount of
 * practice on any one concept moves its bar.
 */
function covers(objective: QuestObjective, conceptId: string, registry: CurriculumRegistry): boolean {
  switch (objective.kind) {
    case 'master-concepts':
    case 'practice':
      return objective.conceptIds.includes(conceptId);
    case 'complete-lessons':
      return objective.lessonIds.some((lessonId) => registry.lesson(lessonId)?.conceptId === conceptId);
    case 'clear-quests':
      return false;
  }
}

function titleFor(recommendation: Recommendation, conceptTitle: string, quest: QuestDef | undefined): string {
  if (quest) return `Continue “${quest.title}” — practise ${conceptTitle}`;
  switch (recommendation.kind) {
    case 'repair':
      return `Reinforce the foundations — ${conceptTitle}`;
    case 'review':
      return `An old skill calls for you — ${conceptTitle}`;
    case 'continue':
    case 'advance':
      return `Your next adventure — ${conceptTitle}`;
  }
}

function hrefFor(recommendation: Recommendation, quest: QuestDef | undefined): string {
  if (quest) return `/adventure/quests/${quest.id}`;
  /*
   * `advance` is a concept the student has never met. Dropping somebody into
   * generated practice on something nobody has taught them is a test, not an
   * adventure; the concept page holds the lesson, the practice and the chain it
   * hangs off, and lets them choose which door to take.
   */
  if (recommendation.kind === 'advance') return `/concepts/${recommendation.conceptId}`;
  return `/practice/${recommendation.conceptId}`;
}
